import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";
import { Building2, MapPin, Calendar, Users, Edit, Activity, Database, ShieldAlert, AlertTriangle, HardDrive } from "lucide-react";

interface Company {
  id: number;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  area_atuacao: string;
  endereco?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  created_at: string;
  company_subscriptions?: Array<{
    id: number;
    status: string;
    started_at: string;
    expires_at?: string;
    subscription_plans?: {
      name: string;
      price: number;
      max_users?: number | null;
      max_services?: number | null;
      max_appointments?: number | null;
    };
  }>;
}

interface AuditItem {
  id: string;
  created_at: string | null;
  operation: string;
  table_name: string;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
}

interface CompanyMonitoringSnapshot {
  usersTotal: number;
  adminUsersTotal: number;
  pacientesTotal: number;
  atendimentosTotal: number;
  atendimentosMes: number;
  anamneseTemplatesTotal: number;
  anamnesesTotal: number;
  produtosTotal: number;
  insumosTotal: number;
  servicosTotal: number;
  orcamentosTotal: number;
  financeiroTotal: number;
  auditTotal: number;
  serviceAttachmentsTotal: number;
  produtosComImagemTotal: number;
  insumosComImagemTotal: number;
  storageByBucket: Array<{
    bucket_id: string;
    files_count: number;
    total_bytes: number;
  }>;
  storageTotalBytes: number;
  deletesLast24h: number;
  lastAtendimentoAt: string | null;
  auditRecent: AuditItem[];
}

type StorageUsageRow = {
  bucket_id: string;
  files_count: number;
  total_bytes: number;
};

interface MonitoringAlert {
  severity: "critical" | "warning" | "info";
  title: string;
  description: string;
}

interface AdminCompanyDetailsDialogProps {
  company: Company | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: (company: Company) => void;
}

export const AdminCompanyDetailsDialog: React.FC<AdminCompanyDetailsDialogProps> = ({
  company,
  open,
  onOpenChange,
  onEdit,
}) => {
  const countOrZero = async (query: PromiseLike<{ count: number | null; error: unknown }>) => {
    const { count, error } = await query;
    if (error) {
      console.error("Erro ao buscar métrica administrativa:", error);
      return 0;
    }

    return count || 0;
  };

  const { data: monitoring, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-company-monitoring", company?.id],
    enabled: open && !!company?.id,
    queryFn: async (): Promise<CompanyMonitoringSnapshot> => {
      const companyId = company?.id;
      if (!companyId) {
        return {
          usersTotal: 0,
          adminUsersTotal: 0,
          pacientesTotal: 0,
          atendimentosTotal: 0,
          atendimentosMes: 0,
          anamneseTemplatesTotal: 0,
          anamnesesTotal: 0,
          produtosTotal: 0,
          insumosTotal: 0,
          servicosTotal: 0,
          orcamentosTotal: 0,
          financeiroTotal: 0,
          auditTotal: 0,
          serviceAttachmentsTotal: 0,
          produtosComImagemTotal: 0,
          insumosComImagemTotal: 0,
          storageByBucket: [],
          storageTotalBytes: 0,
          deletesLast24h: 0,
          lastAtendimentoAt: null,
          auditRecent: [],
        };
      }

      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      const last24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      const [
        usersTotal,
        adminUsersTotal,
        pacientesTotal,
        atendimentosTotal,
        atendimentosMes,
        anamneseTemplatesTotal,
        anamnesesTotal,
        produtosTotal,
        insumosTotal,
        servicosTotal,
        orcamentosTotal,
        financeiroTotal,
        auditTotal,
        serviceAttachmentsTotal,
        produtosComImagemTotal,
        insumosComImagemTotal,
      ] = await Promise.all([
        countOrZero(
          supabase.from("users").select("id", { count: "exact", head: true }).eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase
            .from("users")
            .select("id", { count: "exact", head: true })
            .eq("empresa_id", companyId)
            .eq("nivel", 0),
        ),
        countOrZero(
          supabase.from("pacientes").select("id", { count: "exact", head: true }).eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase.from("atendimentos").select("id", { count: "exact", head: true }).eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase
            .from("atendimentos")
            .select("id", { count: "exact", head: true })
            .eq("empresa_id", companyId)
            .gte("created_at", monthStart.toISOString()),
        ),
        countOrZero(
          supabase
            .from("anamnese_templates")
            .select("id", { count: "exact", head: true })
            .eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase
            .from("anamneses")
            .select("id, atendimentos!inner(empresa_id)", { count: "exact", head: true })
            .eq("atendimentos.empresa_id", companyId),
        ),
        countOrZero(
          supabase.from("produtos").select("id", { count: "exact", head: true }).eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase.from("insumos").select("id", { count: "exact", head: true }).eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase.from("servicos").select("id", { count: "exact", head: true }).eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase.from("orcamentos").select("id", { count: "exact", head: true }).eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase.from("financeiro").select("id", { count: "exact", head: true }).eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase.from("audit_log").select("id", { count: "exact", head: true }).eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase
            .from("servicos_anexos")
            .select("id", { count: "exact", head: true })
            .eq("empresa_id", companyId),
        ),
        countOrZero(
          supabase
            .from("produtos")
            .select("id", { count: "exact", head: true })
            .eq("empresa_id", companyId)
            .not("imagem_url", "is", null),
        ),
        countOrZero(
          supabase
            .from("insumos")
            .select("id", { count: "exact", head: true })
            .eq("empresa_id", companyId)
            .not("image_url", "is", null),
        ),
      ]);

      const { data: auditRecentData, error: auditRecentError } = await supabase
        .from("audit_log")
        .select("id, created_at, operation, table_name, user_id")
        .eq("empresa_id", companyId)
        .order("created_at", { ascending: false })
        .limit(20);

      if (auditRecentError) {
        console.error("Erro ao carregar auditoria recente:", auditRecentError);
      }

      const auditUserIds = Array.from(
        new Set(
          (auditRecentData || [])
            .map((item) => item.user_id)
            .filter((userId): userId is string => Boolean(userId)),
        ),
      );

      const auditUsersById = new Map<string, { name: string | null; email: string | null }>();

      if (auditUserIds.length > 0) {
        const { data: auditUsersData, error: auditUsersError } = await supabase
          .from("users")
          .select("id, name, email")
          .in("id", auditUserIds);

        if (auditUsersError) {
          console.error("Erro ao carregar dados dos usuários da auditoria:", auditUsersError);
        }

        (auditUsersData || []).forEach((user) => {
          auditUsersById.set(user.id, {
            name: user.name,
            email: user.email,
          });
        });
      }

      const auditRecent = (auditRecentData || []).map((item) => {
        const userData = item.user_id ? auditUsersById.get(item.user_id) : null;
        return {
          ...item,
          user_name: userData?.name || null,
          user_email: userData?.email || null,
        };
      }) as AuditItem[];

      const deletesLast24h = await countOrZero(
        supabase
          .from("audit_log")
          .select("id", { count: "exact", head: true })
          .eq("empresa_id", companyId)
          .eq("operation", "DELETE")
          .gte("created_at", last24h),
      );

      const { data: lastAtendimentoData, error: lastAtendimentoError } = await supabase
        .from("atendimentos")
        .select("created_at")
        .eq("empresa_id", companyId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (lastAtendimentoError) {
        console.error("Erro ao buscar última atividade de atendimento:", lastAtendimentoError);
      }

      const supabaseRpc = supabase as unknown as {
        rpc: (
          fn: "admin_company_storage_usage",
          args: { p_empresa_id: number },
        ) => Promise<{ data: StorageUsageRow[] | null; error: unknown }>;
      };

      const { data: storageUsageData, error: storageUsageError } = await supabaseRpc.rpc(
        "admin_company_storage_usage",
        {
          p_empresa_id: companyId,
        },
      );

      if (storageUsageError) {
        console.error("Erro ao carregar uso de storage:", storageUsageError);
      }

      const storageByBucket = Array.isArray(storageUsageData)
        ? storageUsageData.map((row: StorageUsageRow) => ({
            bucket_id: String(row.bucket_id),
            files_count: Number(row.files_count || 0),
            total_bytes: Number(row.total_bytes || 0),
          }))
        : [];

      const storageTotalBytes = storageByBucket.reduce((sum, bucket) => sum + bucket.total_bytes, 0);

      return {
        usersTotal,
        adminUsersTotal,
        pacientesTotal,
        atendimentosTotal,
        atendimentosMes,
        anamneseTemplatesTotal,
        anamnesesTotal,
        produtosTotal,
        insumosTotal,
        servicosTotal,
        orcamentosTotal,
        financeiroTotal,
        auditTotal,
        serviceAttachmentsTotal,
        produtosComImagemTotal,
        insumosComImagemTotal,
        storageByBucket,
        storageTotalBytes,
        deletesLast24h,
        lastAtendimentoAt: lastAtendimentoData?.created_at || null,
        auditRecent,
      };
    },
  });

  if (!company) return null;

  const activeSubscription = company.company_subscriptions?.find(sub => sub.status === 'active');
  const maxUsers = activeSubscription?.subscription_plans?.max_users ?? null;
  const maxServices = activeSubscription?.subscription_plans?.max_services ?? null;
  const maxAppointments = activeSubscription?.subscription_plans?.max_appointments ?? null;

  const seatUsage = monitoring?.usersTotal || 0;
  const servicesUsage = monitoring?.servicosTotal || 0;
  const appointmentsUsage = monitoring?.atendimentosMes || 0;
  const storageTotalBytes = monitoring?.storageTotalBytes || 0;
  const storageByBucket = monitoring?.storageByBucket || [];
  const storageSoftLimitBytes = 2 * 1024 * 1024 * 1024;

  const getUsagePercent = (value: number, limit: number | null | undefined) => {
    if (!limit || limit <= 0) return 0;
    return Math.min(100, Math.round((value / limit) * 100));
  };

  const formatBytes = (bytes: number) => {
    if (!bytes || bytes <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    return `${value.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
  };

  const getBucketLabel = (bucketId: string) => {
    const bucketLabels: Record<string, string> = {
      anamnesis_files: "Arquivos de Anamnese",
      treatment_images: "Imagens de Tratamentos",
      service_attachments: "Anexos de Serviços",
      documents: "Documentos",
      financial_documents: "Documentos Financeiros",
      "supplier-attachments": "Anexos de Fornecedores",
      "product-images": "Imagens de Produtos",
      negotiation_files: "Arquivos de Negociação",
      "gift-images": "Imagens de Brindes",
      "profile-images": "Imagens de Perfil",
      geral: "Arquivos Gerais",
      equipment: "Arquivos de Equipamentos",
    };

    const knownLabel = bucketLabels[bucketId];
    if (knownLabel) {
      return knownLabel;
    }

    return bucketId
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const seatUsagePercent = getUsagePercent(seatUsage, maxUsers);
  const servicesUsagePercent = getUsagePercent(servicesUsage, maxServices);
  const appointmentUsagePercent = getUsagePercent(appointmentsUsage, maxAppointments);
  const storageUsagePercent = Math.min(
    100,
    Math.round((storageTotalBytes / storageSoftLimitBytes) * 100),
  );
  const storagePalette = [
    "#0ea5e9",
    "#22c55e",
    "#f59e0b",
    "#8b5cf6",
    "#ef4444",
    "#14b8a6",
    "#6366f1",
    "#f97316",
  ];

  const storageVisualData = [...storageByBucket]
    .sort((a, b) => b.total_bytes - a.total_bytes)
    .map((bucket, index) => {
      const sharePercent = storageTotalBytes > 0 ? (bucket.total_bytes / storageTotalBytes) * 100 : 0;

      return {
        ...bucket,
        sharePercent,
        total_mb: Number((bucket.total_bytes / (1024 * 1024)).toFixed(2)),
        bucket_label: getBucketLabel(bucket.bucket_id),
        color: storagePalette[index % storagePalette.length],
      };
    });

  const storageChartData = storageVisualData.slice(0, 8);
  const storageFilesTotal = storageVisualData.reduce((sum, bucket) => sum + bucket.files_count, 0);
  const largestStorageBucket = storageVisualData[0];
  const storageRemainingBytes = Math.max(0, storageSoftLimitBytes - storageTotalBytes);

  const storageHealth =
    storageUsagePercent >= 95
      ? {
          label: "Crítico",
          badge: "bg-red-100 text-red-800",
        }
      : storageUsagePercent >= 75
        ? {
            label: "Atenção",
            badge: "bg-amber-100 text-amber-800",
          }
        : {
            label: "Saudável",
            badge: "bg-emerald-100 text-emerald-800",
          };

  const getOperationBadge = (operation: string) => {
    if (operation === "INSERT") return "bg-green-100 text-green-800";
    if (operation === "UPDATE") return "bg-blue-100 text-blue-800";
    if (operation === "DELETE") return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  };

  const isSeatsAbovePlan = Boolean(maxUsers && seatUsage > maxUsers);
  const isServicesAbovePlan = Boolean(maxServices && servicesUsage > maxServices);

  const monitoringAlerts: MonitoringAlert[] = [];

  if (isSeatsAbovePlan) {
    monitoringAlerts.push({
      severity: "critical",
      title: "Assentos acima do limite",
      description: `A empresa está usando ${seatUsage} usuários para um plano com limite de ${maxUsers}.`,
    });
  } else if (maxUsers && seatUsagePercent >= 80) {
    monitoringAlerts.push({
      severity: "warning",
      title: "Assentos próximos do limite",
      description: `Uso de assentos em ${seatUsagePercent}% do limite contratado.`,
    });
  }

  if (isServicesAbovePlan) {
    monitoringAlerts.push({
      severity: "critical",
      title: "Serviços acima do plano",
      description: `A empresa possui ${servicesUsage} serviços com limite de ${maxServices}.`,
    });
  } else if (maxServices && servicesUsagePercent >= 80) {
    monitoringAlerts.push({
      severity: "warning",
      title: "Serviços próximos do limite",
      description: `Cadastro de serviços em ${servicesUsagePercent}% da capacidade contratada.`,
    });
  }

  if (maxAppointments && appointmentUsagePercent >= 80) {
    monitoringAlerts.push({
      severity: "warning",
      title: "Atendimentos do mês próximos do limite",
      description: `A empresa consumiu ${appointmentUsagePercent}% da franquia mensal de atendimentos.`,
    });
  }

  if (storageTotalBytes >= 2 * 1024 * 1024 * 1024) {
    monitoringAlerts.push({
      severity: "critical",
      title: "Storage elevado",
      description: `Uso atual de storage em ${formatBytes(storageTotalBytes)}. Recomendado revisar arquivos antigos.`,
    });
  } else if (storageTotalBytes >= 1 * 1024 * 1024 * 1024) {
    monitoringAlerts.push({
      severity: "warning",
      title: "Storage em crescimento",
      description: `Uso atual de storage em ${formatBytes(storageTotalBytes)}.`,
    });
  }

  if ((monitoring?.deletesLast24h || 0) >= 50) {
    monitoringAlerts.push({
      severity: "critical",
      title: "Pico de exclusões nas últimas 24h",
      description: `${monitoring?.deletesLast24h || 0} eventos DELETE registrados no período.`,
    });
  } else if ((monitoring?.deletesLast24h || 0) >= 20) {
    monitoringAlerts.push({
      severity: "warning",
      title: "Volume alto de exclusões nas últimas 24h",
      description: `${monitoring?.deletesLast24h || 0} eventos DELETE no período recente.`,
    });
  }

  if (activeSubscription) {
    if (!monitoring?.lastAtendimentoAt) {
      monitoringAlerts.push({
        severity: "info",
        title: "Sem atendimentos registrados",
        description: "A empresa possui assinatura ativa, mas ainda não registrou atendimentos.",
      });
    } else {
      const inactiveDays = Math.floor(
        (Date.now() - new Date(monitoring.lastAtendimentoAt).getTime()) / (1000 * 60 * 60 * 24),
      );

      if (inactiveDays >= 30) {
        monitoringAlerts.push({
          severity: "warning",
          title: "Queda de atividade operacional",
          description: `Último atendimento registrado há ${inactiveDays} dias.`,
        });
      }
    }
  }

  if (monitoringAlerts.length === 0) {
    monitoringAlerts.push({
      severity: "info",
      title: "Sem alertas críticos",
      description: "Os indicadores principais da empresa estão dentro do comportamento esperado.",
    });
  }

  const getAlertBadgeClass = (severity: MonitoringAlert["severity"]) => {
    if (severity === "critical") return "bg-red-100 text-red-800";
    if (severity === "warning") return "bg-amber-100 text-amber-800";
    return "bg-blue-100 text-blue-800";
  };

  const getAlertLabel = (severity: MonitoringAlert["severity"]) => {
    if (severity === "critical") return "Crítico";
    if (severity === "warning") return "Atenção";
    return "Informativo";
  };

  const InfoItem = ({ label, value }: { label: string; value: string | undefined }) => (
    <div className="space-y-1">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value || 'Não informado'}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Detalhes da Empresa
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Visão 360</TabsTrigger>
            <TabsTrigger value="capacity">Limites e Capacidade</TabsTrigger>
            <TabsTrigger value="alerts">Alertas</TabsTrigger>
            <TabsTrigger value="audit">Auditoria</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Dados Principais
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem label="Razão Social" value={company.razao_social} />
                <InfoItem label="Nome Fantasia" value={company.nome_fantasia} />
                <InfoItem label="CNPJ" value={company.cnpj} />
                <InfoItem label="Área de Atuação" value={company.area_atuacao} />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Endereço
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoItem label="Endereço" value={company.endereco} />
                <InfoItem label="Cidade" value={company.cidade} />
                <InfoItem label="Estado" value={company.estado} />
                <InfoItem label="CEP" value={company.cep} />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Assinatura
              </h3>
              {activeSubscription ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem
                    label="Plano"
                    value={activeSubscription.subscription_plans?.name}
                  />
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <Badge className="bg-green-100 text-green-800">Ativa</Badge>
                  </div>
                  <InfoItem
                    label="Data de Início"
                    value={new Date(activeSubscription.started_at).toLocaleDateString("pt-BR")}
                  />
                  <InfoItem
                    label="Data de Expiração"
                    value={
                      activeSubscription.expires_at
                        ? new Date(activeSubscription.expires_at).toLocaleDateString("pt-BR")
                        : "Sem expiração"
                    }
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhuma assinatura ativa</p>
              )}
            </div>

            <Separator />

            {isLoading ? (
              <div className="text-sm text-muted-foreground">Carregando métricas da empresa...</div>
            ) : isError ? (
              <div className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 p-4">
                <p className="text-sm text-destructive">Não foi possível carregar o monitoramento desta empresa.</p>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Tentar novamente
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Uso Operacional
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Usuários</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{monitoring?.usersTotal || 0}</div>
                        <p className="text-xs text-muted-foreground">{monitoring?.adminUsersTotal || 0} administradores</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Clientes</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{monitoring?.pacientesTotal || 0}</div>
                        <p className="text-xs text-muted-foreground">Base total de pacientes</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Atendimentos</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{monitoring?.atendimentosTotal || 0}</div>
                        <p className="text-xs text-muted-foreground">{monitoring?.atendimentosMes || 0} neste mês</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Anamneses</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{monitoring?.anamnesesTotal || 0}</div>
                        <p className="text-xs text-muted-foreground">
                          {monitoring?.anamneseTemplatesTotal || 0} templates ativos
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Serviços</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{monitoring?.servicosTotal || 0}</div>
                        <p className="text-xs text-muted-foreground">Catálogo de serviços</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Produtos</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{monitoring?.produtosTotal || 0}</div>
                        <p className="text-xs text-muted-foreground">{monitoring?.produtosComImagemTotal || 0} com imagem</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Insumos</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{monitoring?.insumosTotal || 0}</div>
                        <p className="text-xs text-muted-foreground">{monitoring?.insumosComImagemTotal || 0} com imagem</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Financeiro</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{monitoring?.financeiroTotal || 0}</div>
                        <p className="text-xs text-muted-foreground">{monitoring?.orcamentosTotal || 0} orçamentos</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Armazenamento (uso real por bucket)
                  </h3>
                  <Card className="border-slate-200 shadow-sm">
                    <CardContent className="pt-6 space-y-6">
                      <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 p-4">
                        <div className="flex flex-wrap items-end justify-between gap-4">
                          <div>
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                              Total armazenado
                            </p>
                            <div className="text-3xl font-semibold text-slate-900">{formatBytes(storageTotalBytes)}</div>
                            <p className="text-sm text-muted-foreground">
                              {storageByBucket.length} bucket(s) com uso • {storageFilesTotal} arquivo(s)
                            </p>
                          </div>

                          <div className="text-right space-y-1">
                            <Badge className={storageHealth.badge}>{storageHealth.label}</Badge>
                            <p className="text-xs text-muted-foreground">Uso na referência operacional</p>
                            <p className="text-sm font-semibold text-slate-900">
                              {storageUsagePercent}% de {formatBytes(storageSoftLimitBytes)}
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Maior consumo</p>
                            <p className="text-sm font-semibold text-slate-900">
                              {largestStorageBucket ? largestStorageBucket.bucket_label : "Sem dados"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {largestStorageBucket
                                ? `${formatBytes(largestStorageBucket.total_bytes)} • ${largestStorageBucket.sharePercent.toFixed(1)}%`
                                : "Nenhum bucket com consumo"}
                            </p>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Capacidade restante (referência)</p>
                            <p className="text-sm font-semibold text-slate-900">{formatBytes(storageRemainingBytes)}</p>
                            <p className="text-xs text-muted-foreground">
                              Referência operacional configurada em {formatBytes(storageSoftLimitBytes)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {storageVisualData.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Nenhum uso de storage encontrado para a empresa.
                        </p>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">
                              Distribuição por bucket (gráfico horizontal em MB)
                            </p>
                            <ChartContainer
                              config={{
                                total_mb: {
                                  label: "Uso (MB)",
                                  color: "#0ea5e9",
                                },
                              }}
                              className="!aspect-auto h-72 w-full"
                            >
                              <BarChart
                                data={storageChartData}
                                layout="vertical"
                                margin={{ top: 6, right: 18, left: 0, bottom: 6 }}
                              >
                                <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                                <XAxis
                                  type="number"
                                  tickFormatter={(value) => `${Math.round(Number(value))} MB`}
                                />
                                <YAxis
                                  type="category"
                                  dataKey="bucket_label"
                                  width={190}
                                  tickFormatter={(value) => String(value).slice(0, 26)}
                                />
                                <ChartTooltip content={<ChartTooltipContent hideLabel indicator="line" />} />
                                <Bar dataKey="total_mb" radius={4}>
                                  {storageChartData.map((bucket) => (
                                    <Cell
                                      key={`chart-${bucket.bucket_id}`}
                                      fill={bucket.color}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ChartContainer>
                            {storageVisualData.length > storageChartData.length && (
                              <p className="text-[11px] text-muted-foreground">
                                +{storageVisualData.length - storageChartData.length} bucket(s) exibidos abaixo no detalhamento.
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">
                              Consumo individual por bucket
                            </p>
                            {storageVisualData.map((bucket) => (
                              <div key={bucket.bucket_id} className="space-y-1.5">
                                <div className="flex items-center justify-between gap-2 text-xs">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className="h-2.5 w-2.5 rounded-full"
                                      style={{ backgroundColor: bucket.color }}
                                    />
                                    <span className="font-medium">{bucket.bucket_label}</span>
                                  </div>
                                  <span className="text-muted-foreground">
                                    {formatBytes(bucket.total_bytes)} • {bucket.files_count} arquivo(s) • {bucket.sharePercent.toFixed(1)}%
                                  </span>
                                </div>
                                <Progress value={bucket.sharePercent} />
                              </div>
                            ))}
                          </div>

                          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-3">
                            <div className="flex items-center justify-between gap-2 text-xs text-emerald-900">
                              <p className="font-semibold">Bateria de Storage (divisão por bucket)</p>
                              <p>Total consolidado: {formatBytes(storageTotalBytes)}</p>
                            </div>

                            <div className="flex items-center gap-2">
                              <div className="relative h-8 flex-1 rounded-md border border-emerald-300 bg-white px-1 py-1">
                                <div className="flex h-full w-full overflow-hidden rounded-sm bg-emerald-100/60">
                                  {storageVisualData.map((bucket) => (
                                    <div
                                      key={`battery-${bucket.bucket_id}`}
                                      className="h-full"
                                      style={{
                                        width: `${bucket.sharePercent}%`,
                                        backgroundColor: bucket.color,
                                      }}
                                    />
                                  ))}
                                </div>
                                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-emerald-900">
                                  {storageUsagePercent}% da referência ({formatBytes(storageSoftLimitBytes)})
                                </span>
                              </div>
                              <div className="h-3 w-1 rounded-r bg-emerald-300" />
                            </div>

                            <div className="grid grid-cols-1 gap-1 text-[11px] text-emerald-900 sm:grid-cols-2">
                              {storageVisualData.map((bucket) => (
                                <div key={`legend-${bucket.bucket_id}`} className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1.5">
                                    <span
                                      className="h-2 w-2 rounded-full"
                                      style={{ backgroundColor: bucket.color }}
                                    />
                                    <span>{bucket.bucket_label}</span>
                                  </div>
                                  <span className="font-medium">{bucket.sharePercent.toFixed(1)}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}

                      <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                        Métrica em bytes/MB/GB consolidada automaticamente por bucket para esta empresa.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="capacity" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Assentos (Usuários)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>{seatUsage} usados</span>
                    <span>{maxUsers ?? "Ilimitado"} no plano</span>
                  </div>
                  <Progress value={maxUsers ? seatUsagePercent : 0} />
                  {isSeatsAbovePlan && (
                    <div className="flex items-center gap-2 text-xs text-red-700">
                      <ShieldAlert className="h-3.5 w-3.5" />
                      Uso de assentos acima do limite do plano.
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Serviços Cadastrados</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>{servicesUsage} usados</span>
                    <span>{maxServices ?? "Ilimitado"} no plano</span>
                  </div>
                  <Progress value={maxServices ? servicesUsagePercent : 0} />
                  {isServicesAbovePlan && (
                    <div className="flex items-center gap-2 text-xs text-red-700">
                      <ShieldAlert className="h-3.5 w-3.5" />
                      Quantidade de serviços acima do limite do plano.
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Atendimentos no Mês</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>{appointmentsUsage} usados</span>
                    <span>{maxAppointments ?? "Ilimitado"} no plano</span>
                  </div>
                  <Progress value={maxAppointments ? appointmentUsagePercent : 0} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Auditoria</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <div className="text-2xl font-bold">{monitoring?.auditTotal || 0}</div>
                  <p className="text-xs text-muted-foreground">Eventos registrados em log de auditoria</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Storage</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>{formatBytes(storageTotalBytes)} usados</span>
                    <span>{formatBytes(storageSoftLimitBytes)} referência</span>
                  </div>
                  <Progress value={storageUsagePercent} />
                  <p className="text-xs text-muted-foreground">
                    Referência operacional para alertas de crescimento do consumo.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="alerts" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Alertas automáticos da empresa
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {monitoringAlerts.map((alert, index) => (
                  <div key={`${alert.title}-${index}`} className="rounded-md border p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm">{alert.title}</p>
                      <Badge className={getAlertBadgeClass(alert.severity)}>{getAlertLabel(alert.severity)}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{alert.description}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <HardDrive className="h-4 w-4" />
                  Indicadores complementares
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-md border p-3">
                  <p className="text-sm text-muted-foreground">Exclusões nas últimas 24h</p>
                  <p className="text-xl font-bold">{monitoring?.deletesLast24h || 0}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-sm text-muted-foreground">Último atendimento</p>
                  <p className="text-sm font-semibold">
                    {monitoring?.lastAtendimentoAt
                      ? new Date(monitoring.lastAtendimentoAt).toLocaleString("pt-BR")
                      : "Sem registros"}
                  </p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-sm text-muted-foreground">Uso total de storage</p>
                  <p className="text-xl font-bold">{formatBytes(storageTotalBytes)}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Eventos recentes da empresa</CardTitle>
              </CardHeader>
              <CardContent>
                {(monitoring?.auditRecent?.length || 0) === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum evento de auditoria encontrado.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Operação</TableHead>
                        <TableHead>Tabela</TableHead>
                        <TableHead>Usuário</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monitoring?.auditRecent.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            {item.created_at ? new Date(item.created_at).toLocaleString("pt-BR") : "-"}
                          </TableCell>
                          <TableCell>
                            <Badge className={getOperationBadge(item.operation)}>{item.operation}</Badge>
                          </TableCell>
                          <TableCell>{item.table_name}</TableCell>
                          <TableCell>
                            {!item.user_id ? (
                              "Sistema"
                            ) : (
                              <div className="space-y-0.5">
                                <p className="text-sm font-medium">{item.user_name || "Usuário"}</p>
                                <p className="text-xs text-muted-foreground">{item.user_email || "Email não informado"}</p>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Ações */}
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {onEdit && (
            <Button onClick={() => {
              onEdit(company);
              onOpenChange(false);
            }}>
              <Edit className="mr-2 h-4 w-4" />
              Editar Empresa
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
